import React, { useState } from "react";
import {
  ArrowRight,
  ShoppingBag,
  Sparkles,
  CheckCircle2,
  Check,
} from "lucide-react";
import { motion } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import { scroller } from "react-scroll"; // Import scroller

/* ── Color Palette ─────────────────────────────────────────────────────── */
const COLORS = {
  navy: "#1b2f5a",
  orange: "#f7941d",
  bg: "#FDFDFF",
};

/* ── Logo URLs ─────────────────────────────────────────────────────────── */
const SALESFORCE_LOGO = "https://a.sfdcstatic.com/shared/images/c360-nav/salesforce-with-type-logo.svg";
const GOCONTINUUM_LOGO = "http://gocontinuum.ai/hs-fs/hubfs/Continuum_Sr/Images/Continuum%20logo.png?width=394&height=72&name=Continuum%20logo.png";
const MARINEFLOW_LOGO = "https://www.marineflow.ai/images/marineflow-logo-blue.svg";
const NEXUM_LOGO = "https://i0.wp.com/nexumcoffee.com/wp-content/uploads/2024/05/Nexum-with-R-Belshe-quick-edit-John-Belshe.png?w=1858&ssl=1";
const PITCGHSTER_LOGO = "data:image/png;base64,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";

/* ── Project Data ───────────────────────────────────────────────────────── */
const projects = [
  {
    id: 1,
    category: "Maritime",
    title: "MarineFlow AI - Maritime Operations Platform",
    description: "An intelligent maritime operations platform that integrates multiple maritime solutions into one intelligent system, enhancing operational efficiency, reducing manual workload, and minimizing errors through automation and real-time insights.",
    points: ["Enhanced Operational Efficiency", "Minimized Error Reduction", "Improved Compliance"],
    tech: ["React.js", "Java", "Spring Boot"],
    logo: MARINEFLOW_LOGO,
    logoAlt: "MarineFlow AI",
  },
  {
    id: 2,
    category: "Sales Technology",
    title: "Pitchster - AI-Powered Sales Training Platform",
    description: "An AI-powered sales training platform that helps reps prepare, perform, and perfect their pitch. Using voice AI, real meeting transcriptions, and performance analytics, it delivers personalized coaching and faster ramp-up.",
    points: ["Enhanced Pitch Quality", "Faster Deal Closure", "Improved Onboarding Speed"],
    tech: ["React.js", "Tailwind CSS", "Node.js"],
    logo: PITCGHSTER_LOGO,
    logoAlt: "Pitchster",
    darkBg: true,
  },
  {
    id: 3,
    category: "B2B RMA SaaS",
    title: "GoContinuum - RMA Automation Platform",
    description: "An innovative Return Management Automation (RMA) platform tailored specifically for B2B product-based companies, seamlessly integrating with existing business systems to enhance customer satisfaction and streamline post-purchase processes.",
    points: ["Minimized Error Reduction", "Significant Time Savings", "Tons Paper Savings"],
    tech: ["Java SE", "SpringBoot", "Apache Tomcat Server"],
    logo: GOCONTINUUM_LOGO,
    logoAlt: "GoContinuum",
    darkBg: true,
  },
  {
    id: 4,
    category: "Retail & Distribution",
    title: "DMC - Salesforce Field Service Lightning",
    description: "Implementation of Salesforce Field Service Lightning to streamline field service operations, automate work order management, and enhance customer service delivery for Delta Marketing Company.",
    points: ["Faster Service Delivery", "Increased Technician Productivity", "Improved Customer Satisfaction"],
    tech: ["Salesforce", "Field Service Lightning (FSL)", "Salesforce Mobile App"],
    logo: SALESFORCE_LOGO,
    logoAlt: "Salesforce",
    darkBg: true,
  },
  {
    id: 5,
    category: "E-Commerce",
    title: "Nexum - Coffee E-Commerce Platform",
    description: "An innovative e-commerce platform that revolutionizes the coffee industry by connecting farmers directly with consumers, providing a seamless experience for users from product browsing to transactions.",
    points: ["Seamless Platform Connectivity", "Secure Payment Security", "Accurate Inventory Accuracy"],
    tech: ["JavaScript", "HTML", "CSS"],
    logo: NEXUM_LOGO,
    logoAlt: "Nexum Coffee",
  },
  {
    id: 6,
    category: "Logistics & Payments",
    title: "CargoSprint - Salesforce Payment Integration",
    description: "A comprehensive Salesforce implementation integrating Google Cloud services, Certinia for enhanced security, and a custom payment gateway to modernize CRM infrastructure and streamline payment data integration.",
    points: ["Improved Operational Efficiency", "Enhanced Customer Experience", "Achieved Cost Savings"],
    tech: ["Salesforce", "Google Cloud Platform", "Certinia (Financial Force)"],
    logo: SALESFORCE_LOGO,
    logoAlt: "Salesforce",
    darkBg: true,
  },
  {
    id: 7,
    category: "Events Services",
    title: "Festival Outfitter",
    description: "Festival Outfitter is an event services contractor, with a focus on elevating retail operations via investments in aesthetics, customer service, and product quality. Specializations include merchandising, campsite rentals & glamping programs.",
    points: ["Quality Brands", "Event Aesthetics", "Customer Service"],
    tech: ["Merchandising", "Campsite Rentals", "Glamping"],
    logo: null,
    logoAlt: "Festival Outfitter",
    logoIcon: true,
  },
];

/* ── Project Logo Component ─────────────────────────────────────────────── */
const ProjectLogo = ({ project }) => {
  const [imgError, setImgError] = useState(false);

  const logoContent = (() => {
    if (project.logo && !imgError) {
      return (
        <img
          src={project.logo}
          alt={project.logoAlt}
          className="max-h-7 max-w-[110px] object-contain transition-transform group-hover:scale-105 duration-500"
          onError={() => setImgError(true)}
        />
      );
    }
    if (project.logoIcon) {
      return (
        <div className="flex items-center gap-2">
          <div className="p-1.5 bg-orange-50 rounded-lg">
            <ShoppingBag className="w-4 h-4 text-[#f7941d]" />
          </div>
          <span className={`font-bold text-sm tracking-tight ${project.darkBg ? 'text-white' : 'text-[#1b2f5a]'}`}>{project.logoAlt}</span>
        </div>
      );
    }
    return (
      <div className="flex items-center gap-2">
        <div className="p-1.5 bg-slate-100 rounded-lg">
          <Sparkles className="w-4 h-4 text-[#1b2f5a]" />
        </div>
        <span className={`font-bold text-sm tracking-tight ${project.darkBg ? 'text-white' : 'text-[#1b2f5a]'}`}>
          {project.logoAlt}
        </span>
      </div>
    );
  })();

  if (project.darkBg) {
    return (
      <div className="bg-[#1b2f5a] px-4 py-2 rounded-xl flex items-center justify-center min-w-[120px] shadow-sm">
        {logoContent}
      </div>
    );
  }
  return logoContent;
};

/* ── Project Card Component ─────────────────────────────────────────────── */
const ProjectCard = ({ project }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.1 }}
      whileHover={{ y: -8 }}
      className="group relative bg-white rounded-[2rem] p-8 shadow-[0_10px_30px_-10px_rgba(27,47,90,0.08)] hover:shadow-[0_20px_50px_-15px_rgba(247,148,29,0.15)] border border-slate-100 transition-all duration-500 flex flex-col h-full overflow-hidden"
    >
      <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-orange-50/50 to-transparent rounded-full blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none" />
      <div className="flex items-center justify-between mb-8 relative z-10">
        <div className="h-12 flex items-center">
          <ProjectLogo project={project} />
        </div>
        <span className="bg-orange-50 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest text-[#f7941d] border border-orange-100">
          {project.category}
        </span>
      </div>
      <div className="relative z-10">
        <h3 className="text-xl font-bold text-[#1b2f5a] mb-4 leading-tight group-hover:text-[#f7941d] transition-colors">
          {project.title}
        </h3>
        <p className="text-slate-500 text-sm leading-relaxed mb-8">
          {project.description}
        </p>
        <div className="space-y-4 mb-2">
          {project.points.map((point, idx) => (
            <div key={idx} className="flex items-start gap-3 text-sm font-medium text-slate-600 group/item">
              <div className="flex-shrink-0 mt-1">
                <Check className="w-4 h-4 text-[#f7941d] transition-transform duration-300 group-hover/item:scale-110" strokeWidth={3} />
              </div>
              <span className="leading-tight transition-colors group-hover/item:text-[#1b2f5a]">{point}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="flex flex-wrap gap-2 mb-4 mt-auto relative z-10 pt-6 border-t border-slate-50">
        {project.tech.map((t, idx) => (
          <span key={idx} className="bg-slate-50 text-slate-500 px-3 py-1 rounded-lg text-[10px] font-bold uppercase tracking-tight border border-slate-100 group-hover:bg-[#1b2f5a] group-hover:text-white group-hover:border-[#1b2f5a] transition-all">
            {t}
          </span>
        ))}
      </div>
    </motion.div>
  );
};

/* ── Main Portfolio Component ───────────────────────────────────────────── */
const Portfolio = () => {
  const navigate = useNavigate();
  const location = useLocation();

  /* Helper function to handle scroll or redirect */
  const handleScrollToContact = () => {
    if (location.pathname === "/") {
      // If already on home, scroll to the id
      scroller.scrollTo("contact", {
        duration: 800,
        smooth: "easeInOutQuart",
        offset: -80, // Matches your header offset
      });
    } else {
      // If on a sub-route, navigate home and pass state to scroll
      navigate("/", { replace: true, state: { scrollTo: "contact" } });
    }
  };
  
  return (
    <section className="relative min-h-screen bg-[#FDFDFF] py-24 px-6 overflow-visible">
      <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-slate-100/40 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-[-10%] right-[-10%] w-[30%] h-[40%] bg-orange-50/40 rounded-full blur-[120px] pointer-events-none" />

      <div className="max-w-7xl mx-auto relative z-10">
        <motion.div
          whileInView={{ opacity: 1, y: 0 }}
          initial={{ opacity: 0, y: 40 }}
          transition={{ duration: 0.5 }}
          className="text-center md:text-left mb-16"
        >
          <p className="font-semibold text-sm uppercase tracking-wider mb-4 text-[#f7941d]">
           Customer Stories
          </p>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 leading-tight">
            Customer Success Stories
          </h2>
          <div className="w-16 h-1 mt-4 mx-auto md:mx-0 bg-[#f7941d]"></div>
          
          <p className="text-slate-500 text-lg md:text-xl mt-8 max-w-4xl md:mx-0 mx-auto leading-relaxed">
            Explore how BytesFarms Technologies has partnered with leading enterprises and innovative startups to deliver transformative technology solutions and achieve remarkable results.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10 mb-10">
          {projects.map((project) => (
            <ProjectCard key={project.id} project={project} />
          ))}

          {/* Dynamic CTA Card */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.1 }}
            className="group relative bg-[#1b2f5a] rounded-[2.5rem] p-8 md:p-14 shadow-[0_25px_60px_-15px_rgba(27,47,90,0.4)] border border-[#1b2f5a] transition-all duration-500 flex flex-col h-full overflow-hidden col-span-1 md:col-span-1 lg:col-span-2"
          >
            <div className="absolute top-0 right-0 w-full h-full bg-[radial-gradient(circle_at_top_right,rgba(247,148,29,0.1),transparent_50%)] pointer-events-none" />
            <div className="absolute -bottom-24 -left-24 w-80 h-80 bg-white/5 rounded-full blur-3xl group-hover:scale-110 transition-transform duration-700 pointer-events-none" />
            
            <div className="relative z-10 flex flex-col lg:flex-row lg:items-center justify-between gap-10 lg:gap-16">
              <div className="lg:w-[65%] text-left">
                <div className="w-14 h-14 bg-white/10 rounded-2xl flex items-center justify-center mb-8 shadow-inner border border-white/10">
                  <Sparkles className="w-7 h-7 text-[#f7941d]" />
                </div>
                
                <h3 className="text-4xl md:text-5xl font-extrabold text-white mb-6 tracking-tight leading-none">
                  Ready to <span className="text-[#f7941d]">Scale?</span>
                </h3>
                
                <p className="text-slate-300 text-lg leading-relaxed mb-10">
                  Partner with BytesFarms to transform your vision into an industry-leading reality. Our expert engineers are ready to accelerate your digital journey.
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[
                    "Strategic Consulting",
                    "Custom Development",
                    "AI & Automation",
                    "24/7 Tech Support"
                  ].map((item, idx) => (
                    <div key={idx} className="flex items-center gap-3 text-slate-200 font-medium text-sm">
                      <CheckCircle2 className="w-5 h-5 text-[#f7941d] flex-shrink-0" />
                      {item}
                    </div>
                  ))}
                </div>
              </div>

              <div className="lg:w-[30%] flex flex-col items-center justify-center gap-6">
                <div className="w-full h-px bg-white/10 lg:hidden" />
                
                <div className="space-y-4 w-full">
                  <motion.button
                    whileHover={{ scale: 1.03, y: -2 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleScrollToContact} // Updated this line
                    className="w-full flex items-center justify-center gap-3 bg-[#f7941d] text-white font-extrabold text-lg px-8 py-5 rounded-2xl transition-all duration-300 group shadow-[0_20px_40px_-10px_rgba(247,148,29,0.3)] relative overflow-hidden cursor-pointer hover:bg-white hover:text-[#1b2f5a]"
                  >
                    <span className="relative z-10">Know More</span>
                    <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1 duration-300 relative z-10" />
                  </motion.button>
                  
                  <p className="text-white/40 text-[10px] uppercase tracking-[0.25em] font-bold text-center">
                    Start your journey today
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Portfolio;